/**
 * OcrAdminDetail — 管理员图片详情页 OCR 四个 Tabs
 * 对应 demo view-admin-ocr：
 *   Tab 成功：phone 配对组（展开图片）
 *   Tab 重复：橙色边框 + 来源批次说明
 *   Tab 无效：红色边框 + 手动填写/重新识别
 *   Tab 失败：网格图 + 手动填写/重新识别
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Modal, { useConfirm } from './Modal';
import { useToast } from './Toast';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { Check, AlertTriangle } from 'lucide-react';
import { OriginLine, describeOrigin } from './DuplicateOrigin';

// 格式化手机号：直接返回连续11位数字，不加空格
function fmtPhone(p) {
  if (!p) return '';
  return String(p).replace(/\D/g, '');
}

export default function OcrAdminDetail({ userId, userLabel, onDataChange }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { subscribe } = useSocket();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups]   = useState([]);  // 每个批次 { batchId, batchLabel, createdAt, ocr, images }
  const [tab, setTab]         = useState('qualified');
  const [expandedPairs, setExpandedPairs] = useState({});
  const [lightboxUrl, setLightboxUrl] = useState(null); // 点击放大预览的图片 URL
  // 各 Tab 当前页（切换 Tab 时重置）
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  // 手动填写
  const [manualTarget, setManualTarget] = useState(null); // { imageId, groupLabel }
  const [manualPhone, setManualPhone]   = useState('');
  const [manualLoading, setManualLoading] = useState(false);
  const [manualMsg, setManualMsg] = useState(null); // { type: ok|warn|error, text }
  // 批量重新识别失败（仅管理员可见使用——重跑OCR要消耗调用额度，代理不该能触发）
  const [bulkReocrLoading, setBulkReocrLoading] = useState(false);
  // 提醒补图（缺少配对图时，手动推送提醒用户检查是否漏传）
  const [remindingId, setRemindingId] = useState(null);
  const [remindedIds, setRemindedIds] = useState(() => new Set());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/images/user/${userId}`);
      const raw = res.data;
      // 展平：每个批次取 OCR 汇总 + 图片
      const flatGroups = [];
      for (const dateGroup of (raw.groups || [])) {
        for (const batch of (dateGroup.batches || [])) {
          const images = (batch.images || []).map((img, idx) => ({
            ...img,
            batchSeq    : idx + 1,                      // 该图在本批次里是第几张（1起）
            totalInBatch: (batch.images || []).length,  // 本批次共几张
            originBatch : img.originBatch || null,      // duplicate 专属：首次成功配对批次
          }));
          flatGroups.push({
            batchId    : batch.batchId,
            batchLabel : `${dateGroup.date} · ${batch.batchLabel}`,
            createdAt  : batch.createdAt,
            ocr        : batch.ocr || {},
            images,
          });
        }
      }
      setGroups(flatGroups);
    } catch (e) {
      console.error('[OcrAdminDetail] fetchData error', e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 实时刷新：图片被删除/替换/重新识别等操作都会广播这些事件，
  // 之前这里没有订阅，导致在别处（如图片管理网格）删掉一张失败/无效/重复的图后，
  // 这里的 成功/重复/无效/失败 四格汇总数字不会跟着刷新——图没了但数字没变。
  useEffect(() => {
    if (!subscribe) return;
    // 防抖：ocr_progress 每张图都推，不节流会反复全量 fetchData 导致闪屏
    // ocr_batch_done / image_stats_refresh 完成后立即刷新（500ms）
    // stats_refresh 高频事件，1000ms 防抖
    let progressTimer = null;
    let statsTimer = null;
    return subscribe((event) => {
      if (event === 'ocr_progress') {
        // OCR 逐张进度：防抖 800ms，识别期间不反复刷
        clearTimeout(progressTimer);
        progressTimer = setTimeout(() => fetchData(), 800);
      } else if (event === 'ocr_batch_done' || event === 'image_stats_refresh') {
        // 批次完成或图片变动：500ms 后刷新一次
        clearTimeout(progressTimer);
        clearTimeout(statsTimer);
        setTimeout(() => fetchData(), 500);
      } else if (event === 'stats_refresh') {
        // 全局统计刷新：防抖 1500ms，避免连续多个操作反复触发
        clearTimeout(statsTimer);
        statsTimer = setTimeout(() => fetchData(), 1500);
      }
    });
  }, [subscribe, fetchData]);

  if (loading) return <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: 'var(--c-text-secondary)' }}>加载中…</div>;

  // 所有图片按 ocrResult 分组（跨批次）
  const allImgs = groups.flatMap(g => g.images.map(img => ({ ...img, batchLabel: g.batchLabel })));

  // 建配对 Map：phone → [img1, img2]（qualified + manual_set）
  const pairMap = new Map();
  for (const img of allImgs) {
    if (img.ocrResult !== 'qualified' && img.ocrResult !== 'manual_set') continue;
    const key = img.ocrPhone || `manual_${img.id}`;
    if (!pairMap.has(key)) pairMap.set(key, { phone: img.ocrPhone, imgs: [], isManual: false, maxConf: null });
    const entry = pairMap.get(key);
    entry.imgs.push(img);
    if (img.ocrResult === 'manual_set') entry.isManual = true;
    if (img.ocrConfidence && (!entry.maxConf || img.ocrConfidence > entry.maxConf)) {
      entry.maxConf = img.ocrConfidence;
    }
  }
  const duplicates  = allImgs.filter(i => i.ocrResult === 'duplicate');
  // 无效：识别到号码但配对有问题（不在库/过期），且是2张配对的情况（有组的概念）
  const invalids    = allImgs.filter(i => (i.ocrResult || '').startsWith('invalid'));
  // 失败：OCR技术失败 + 识别不到号码 + 单张缺配对（都是"张"单位，没有"组"的概念）
  const faileds     = allImgs.filter(i => i.ocrResult === 'failed');
  // 单张缺配对 → 移入失败Tab展示（语义是"张"，不是"组"）
  const singleImgs  = invalids.filter(i => i.ocrResult === 'invalid_single');
  // 识别不到号码 → 失败Tab
  const notextImgs  = invalids.filter(i => i.ocrResult === 'invalid_notext' || i.ocrResult === 'invalid');
  // 无效Tab只展示：过期 + 不在库（2张配对的组，单位是"组"）
  const invalidDisplayImgs = invalids.filter(i =>
    i.ocrResult !== 'invalid_notext' &&
    i.ocrResult !== 'invalid' &&
    i.ocrResult !== 'invalid_single'  // 单张缺配对挪到失败Tab
  );

  // 过期和不在库的按号码分组，同号码两张并排展示
  function groupByPhone(imgs) {
    const map = new Map();
    for (const img of imgs) {
      const key = img.ocrPhone || `nokey_${img.id}`;
      if (!map.has(key)) map.set(key, { phone: img.ocrPhone, imgs: [] });
      map.get(key).imgs.push(img);
    }
    return [...map.values()];
  }
  const expiredGroups = groupByPhone(invalids.filter(i => i.ocrResult === 'invalid_expired'));
  const nopoolGroups  = groupByPhone(invalids.filter(i => i.ocrResult === 'invalid_nopool'));

  // 重复：按手机号分组（同一号码的多张图算1组）
  const duplicateGroups = groupByPhone(duplicates);
  const duplicateGroupCount = duplicateGroups.length;

  // 无效：2张配对但不在库/过期（纯"组"单位）
  const invalidGroups = [...expiredGroups, ...nopoolGroups];
  const invalidGroupCount  = invalidGroups.length;

  const counts = {
    qualified : pairMap.size,           // 成功：组数
    duplicate : duplicateGroupCount,    // 重复：组数
    invalid   : invalidGroupCount,      // 无效：纯"组"，只含2张配对不在库/过期
    failed    : faileds.length + notextImgs.length + singleImgs.length, // 失败：纯"张"，含单张缺配对
  };

  // 真正会被"批量重新识别"重跑 OCR 的张数：faileds + notextImgs。
  // 不含 singleImgs（缺配对图）——号码已经识别对了，缺的是配对图，重跑 OCR 没用，
  // 服务端 /reocr-bulk 也不会处理这部分。
  const reocrEligibleCount = faileds.length + notextImgs.length;

  // Tabs 定义
  const tabs = [
    { key: 'qualified', label: '成功', unit: '组', color: 'var(--c-success)',        count: counts.qualified },
    { key: 'duplicate', label: '重复', unit: '组', color: 'var(--c-accent)',         count: counts.duplicate },
    { key: 'invalid',   label: '无效', unit: '组', color: 'var(--c-danger)',         count: counts.invalid },
    { key: 'failed',    label: '失败', unit: '张', color: 'var(--c-text-secondary)', count: counts.failed },
  ];

  // 手动填写处理
  const handleManualSubmit = async () => {
    if (!/^1[3-9]\d{9}$/.test(manualPhone)) return;
    setManualLoading(true);
    setManualMsg(null);
    try {
      await api.post(`/images/ocr-edit/${manualTarget.imageId}`, { phone: manualPhone });
      setManualMsg({ type: 'ok', text: '已保存' });
      fetchData();
      onDataChange && onDataChange();
      setTimeout(() => setManualTarget(null), 1200);
    } catch (err) {
      setManualMsg({ type: 'error', text: err.response?.data?.error || '保存失败' });
    } finally {
      setManualLoading(false);
    }
  };

  // 重新识别（单张）
  const handleReocr = async (imageId) => {
    try {
      const r = await api.post('/images/reocr-single', { imageId: String(imageId) });
      showToast(r?.data?.message || '已加入重新识别队列', 'success');
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.error || '操作失败', 'error');
    }
  };

  // 提醒补图：号码已识别但批次内缺配对图，很多时候是用户漏传了一张，
  // 手动推送一条站内消息让用户自己检查、补充上传——复用现成的 /admin/push/send
  // 通道（socket + Web Push + FCM 三路送达，与结算通知等推送完全一致）
  const handleRemindMissing = async (img) => {
    setRemindingId(img.id);
    try {
      await api.post('/admin/push/send', {
        userId,
        title: '补图提醒',
        content: `您在 ${img.batchLabel} 提交的号码 ${img.ocrPhone || ''} 目前只识别到 1 张截图，缺少配对图，请检查是否漏传，尽快补充上传。`,
      });
      showToast('已提醒该用户补充图片', 'success');
      setRemindedIds(prev => new Set(prev).add(img.id));
    } catch (err) {
      showToast(err.response?.data?.error || '提醒失败', 'error');
    } finally {
      setRemindingId(null);
    }
  };

  // 批量重新识别：该用户所有"失败"（识别不出号码/技术失败）的图统一重跑 OCR
  // 仅管理员可见使用——重跑消耗 OCR 调用额度，代理误点会浪费调用
  const handleBulkReocr = async () => {
    const ok = await confirm('对该用户所有"失败"图片重新跑一遍 OCR 识别？', {
      detail: '缺少配对图、无效不在库/过期的情况不在此范围，此操作将消耗 OCR 调用额度。',
      danger: false,
    });
    if (!ok) return;
    setBulkReocrLoading(true);
    try {
      const r = await api.post('/images/reocr-bulk', { userId });
      showToast(r?.data?.message || '已加入重新识别队列', 'success');
      setTimeout(fetchData, 1500);
    } catch (err) {
      showToast(err.response?.data?.error || '批量重新识别失败', 'error');
    } finally {
      setBulkReocrLoading(false);
    }
  };

  // 失败原因：服务端按 ocr_diag 给出"发生了什么 + 可以怎么办"；老数据没有诊断时回退到原文案
  const failText = (img, fallback) => (img.ocrFail ? `${img.ocrFail.label}。${img.ocrFail.hint}` : fallback);

  // 图片缩略图（共用）
  const ImgThumb = ({ img, dim = 52, h = 92, border }) => (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <img
        src={img.thumbUrl || img.url} alt=""
        style={{ width: dim, height: h, objectFit: 'cover', borderRadius: 6,
                 border: border || '1.5px solid var(--c-border)', display: 'block',
                 cursor: 'zoom-in' }}
        onClick={() => setLightboxUrl(img.url)}
        onError={e => { e.target.style.display = 'none'; }}
      />
      {img.ocrResult === 'manual_set' && (
        <div style={{ position: 'absolute', bottom: 3, right: 3, background: 'var(--c-purple)',
                      borderRadius: 3, padding: '1px 4px', fontSize: 8, color: '#fff', fontWeight: 700 }}>手动</div>
      )}
    </div>
  );

  // ── 通用翻页组件 ───────────────────────────────────────────────────────────
  const Pagination = ({ total, current, onChange }) => {
    const totalPages = Math.ceil(total / PAGE_SIZE);
    if (totalPages <= 1) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px 0 4px' }}>
        <button
          onClick={() => onChange(current - 1)}
          disabled={current <= 1}
          style={{
            padding: '5px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: '1px solid var(--c-border)', background: 'var(--c-bg)',
            color: current <= 1 ? 'var(--c-text-tertiary)' : 'var(--c-primary)',
            cursor: current <= 1 ? 'not-allowed' : 'pointer', opacity: current <= 1 ? 0.4 : 1,
          }}
        >‹ 上一页</button>
        <span style={{ fontSize: 13, color: 'var(--c-text-secondary)', minWidth: 80, textAlign: 'center' }}>
          第 {current} / {totalPages} 页
        </span>
        <button
          onClick={() => onChange(current + 1)}
          disabled={current >= totalPages}
          style={{
            padding: '5px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: '1px solid var(--c-border)', background: 'var(--c-bg)',
            color: current >= totalPages ? 'var(--c-text-tertiary)' : 'var(--c-primary)',
            cursor: current >= totalPages ? 'not-allowed' : 'pointer', opacity: current >= totalPages ? 0.4 : 1,
          }}
        >下一页 ›</button>
      </div>
    );
  };

  return (
    <div>
      {/* 批量重新识别失败：仅管理员可见使用（重跑消耗OCR调用额度） */}
      {isAdmin && reocrEligibleCount > 0 && (
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={handleBulkReocr}
            disabled={bulkReocrLoading}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '8px 0', borderRadius: 10, border: '1px solid var(--c-border)',
              background: bulkReocrLoading ? 'var(--c-bg)' : 'var(--c-surface)',
              color: 'var(--c-text-secondary)', fontSize: 12, fontWeight: 600,
              cursor: bulkReocrLoading ? 'not-allowed' : 'pointer', opacity: bulkReocrLoading ? 0.65 : 1,
            }}
          >
            {bulkReocrLoading ? '识别中…' : `批量重新识别失败（${reocrEligibleCount}张）`}
          </button>
          {/* 提示：重识后仍失败说明图片本身 OCR 读不出号码，需手动填写 */}
          {!bulkReocrLoading && (
            <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 5, padding: '0 2px', lineHeight: 1.5 }}>
              💡 重新识别后仍然失败，说明 OCR 无法从图片中读取号码（图片模糊、遮挡或格式特殊）。
              此时请用「手动填写」直接输入号码，与同批已识别的图片配对完成。
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--c-border)', marginBottom: 14 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setPage(1); }} style={{
            padding: '8px 14px', fontSize: 13, fontWeight: tab === t.key ? 700 : 400,
            color: tab === t.key ? t.color : 'var(--c-text-secondary)',
            borderBottom: tab === t.key ? `2px solid ${t.color}` : '2px solid transparent',
            marginBottom: -2, background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5
          }}>
            {t.label}
            <span style={{ fontSize: 10, background: tab === t.key ? t.color + '22' : 'var(--c-bg)', color: tab === t.key ? t.color : 'var(--c-text-secondary)', borderRadius: 10, padding: '1px 6px', fontWeight: 600 }}>{t.count}{t.unit}</span>
          </button>
        ))}
      </div>

      {/* Tab 内容：成功 */}
      {tab === 'qualified' && (() => {
        const allPairs = [...pairMap.values()];
        const totalPairs = allPairs.length;
        const pagePairs = allPairs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
        return (
          <div>
            {totalPairs === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--c-text-tertiary)', fontSize: 13, padding: 20 }}>暂无成功配对</p>
            ) : (
              <>
                <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginBottom: 8, textAlign: 'right' }}>
                  共 {totalPairs} 组，显示第 {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalPairs)} 组
                </div>
                {pagePairs.map(({ phone, imgs, isManual, maxConf }) => {
                  const key = phone || `manual_${imgs[0]?.id}`;
                  const isOpen = !!expandedPairs[key];
                  return (
                    <div key={key} style={{ border: `1px solid ${isManual ? 'var(--c-purple)' : 'var(--c-border)'}`, borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
                      <div style={{ padding: '9px 12px', background: isManual ? 'rgba(88,86,214,.06)' : 'var(--c-bg)', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                        onClick={() => setExpandedPairs(p => ({ ...p, [key]: !p[key] }))}>
                        <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '.05em', color: isManual ? 'var(--c-purple)' : 'var(--c-text)', flex: 1 }}>{fmtPhone(phone)}</span>
                        <span style={{ fontSize: 10, background: isManual ? 'rgba(88,86,214,.14)' : 'rgba(52,199,89,.12)', color: isManual ? 'var(--c-purple)' : 'var(--c-success)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>{isManual ? '手动确认' : '匹配成功'}</span>
                        {maxConf && (
                          <span style={{ fontSize: 10, marginLeft: 4, color: maxConf >= 80 ? 'var(--c-success)' : maxConf >= 60 ? 'var(--c-accent)' : 'var(--c-danger)', display: 'flex', alignItems: 'center', gap: 2 }}>
                            {maxConf >= 80 ? <Check size={9} strokeWidth={2.5} /> : <AlertTriangle size={9} strokeWidth={2.5} />} OCR {maxConf}%{maxConf < 80 ? '，建议核实' : ''}
                          </span>
                        )}
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--c-text-tertiary)" strokeWidth="2" style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '.15s' }}><polyline points="6 9 12 15 18 9"/></svg>
                      </div>
                      {isOpen && (
                        <div style={{ padding: '8px 12px', display: 'flex', gap: 8, background: 'var(--c-surface)', flexWrap: 'wrap' }}>
                          {imgs.map(img => <ImgThumb key={img.id} img={img} border={isManual ? '1.5px solid var(--c-purple)' : undefined} />)}
                          <div style={{ width: '100%', fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 2 }}>
                            {imgs[0]?.batchLabel}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                <Pagination total={totalPairs} current={page} onChange={setPage} />
              </>
            )}
          </div>
        );
      })()}

      {/* Tab 内容：重复 — 按号码分组展示（跟"成功"一致，而不是每张图单独一卡） */}
      {tab === 'duplicate' && (() => {
        const dupMap = new Map(); // phone -> { phone, imgs: [] }
        for (const img of duplicates) {
          const key = img.ocrPhone || `dup_${img.id}`;
          if (!dupMap.has(key)) dupMap.set(key, { phone: img.ocrPhone, imgs: [] });
          dupMap.get(key).imgs.push(img);
        }
        const allGroups = [...dupMap.values()];
        const totalGroups = allGroups.length;
        const pageGroups = allGroups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
        return (
          <div>
            {totalGroups === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--c-text-tertiary)', fontSize: 13, padding: 20 }}>无重复号码</p>
            ) : (
              <>
                {totalGroups > PAGE_SIZE && (
                  <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginBottom: 8, textAlign: 'right' }}>
                    共 {totalGroups} 组，显示第 {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalGroups)} 组
                  </div>
                )}
                {pageGroups.map(({ phone, imgs }) => {
                  // 同号码所有图共享同一来源，取组内任意一张的 originBatch
                  const origin = imgs.find(i => i.originBatch)?.originBatch || null;
                  const od = describeOrigin(origin, 'admin');
                  // 跨人撞号用红色，和"自己传重"的橙色一眼区分（此前 isSameUser 写死为 true，永远分不出来）
                  const isCross = od.crossUser === true;
                  const tone = isCross ? 'var(--c-danger)' : 'var(--c-accent)';
                  const toneBg = isCross ? 'rgba(255,59,48,.06)' : 'rgba(255,149,0,.06)';
                  const toneChip = isCross ? 'rgba(255,59,48,.15)' : 'rgba(255,149,0,.15)';
                  return (
                  <div key={phone || imgs[0].id} style={{ border: `1px solid ${tone}`, borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
                    <div style={{ padding: '9px 12px', background: toneBg, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: tone, flex: 1 }}>{fmtPhone(phone) || '—'}</span>
                      <span style={{ fontSize: 10, background: toneChip, color: tone, borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>
                        {isCross ? '与他人撞号' : '号码重复'} · {imgs.length}张图片
                      </span>
                      <button
                        onClick={() => { sessionStorage.setItem('phoneQueryPrefill', phone || ''); navigate('/admin/phone-query'); }}
                        style={{ fontSize: 10, padding: '2px 8px', borderRadius: 6, border: '1px solid var(--c-border)',
                          background: 'var(--c-surface)', color: 'var(--c-primary)', cursor: 'pointer', flexShrink: 0 }}>
                        溯源查询
                      </button>
                    </div>
                    <div style={{ padding: '8px 12px', background: 'var(--c-surface)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {imgs.map((img, idx) => (
                        <div key={img.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <ImgThumb img={img} />
                          <div style={{ fontSize: 11, color: 'var(--c-text-secondary)' }}>
                            <div style={{ fontWeight: 600, color: 'var(--c-text)' }}>
                              第 {img.batchSeq} 张
                              {img.totalInBatch ? <span style={{ color: 'var(--c-text-tertiary)', fontWeight: 400 }}>/{img.totalInBatch}</span> : null}
                            </div>
                            <div>{img.batchLabel}</div>
                            {img.uploadedAt && <div style={{ color: 'var(--c-text-tertiary)' }}>{img.uploadedAt.slice(0, 16).replace('T', ' ')}</div>}
                          </div>
                        </div>
                      ))}
                      <div style={{ flexBasis: '100%', marginTop: 4 }}>
                        <OriginLine
                          origin={origin}
                          audience="admin"
                          onOpenImage={(im) => setLightboxUrl(im.url)}
                          onGoUser={(uid) => navigate(`/admin/images/${uid}`)}
                        />
                      </div>
                    </div>
                  </div>
                  );
                })}
                <Pagination total={totalGroups} current={page} onChange={setPage} />
              </>
            )}
          </div>
        );
      })()}

      {/* Tab 内容：无效 — 识别到号码但配对有问题 */}
      {tab === 'invalid' && (() => {
        const allInvalidGroups = [...expiredGroups, ...nopoolGroups];
        const totalInvalid = allInvalidGroups.length;
        const pageInvalidGroups = allInvalidGroups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
        // 确定每组属于哪个子类（expired vs nopool），以便复用渲染逻辑
        const expiredSet = new Set(expiredGroups.map(g => g.phone || `nokey_${g.imgs[0]?.id}`));
        return (
          <div>
            {invalidDisplayImgs.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--c-text-tertiary)', fontSize: 13, padding: 20 }}>无无效图片</p>
            ) : (
              <>
                {totalInvalid > PAGE_SIZE && (
                  <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginBottom: 8, textAlign: 'right' }}>
                    共 {totalInvalid} 组，显示第 {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalInvalid)} 组
                  </div>
                )}
                {pageInvalidGroups.map(({ phone, imgs }) => {
                  const key = phone || `nokey_${imgs[0]?.id}`;
                  const isExpiredGroup = expiredSet.has(key);
                  return isExpiredGroup ? (
                    <div key={key} style={{ border: '1px solid var(--c-danger)', borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
                      <div style={{ padding: '9px 12px', background: 'rgba(255,59,48,.06)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-danger)', flex: 1 }}>{fmtPhone(phone) || '—'}</span>
                        <span style={{ fontSize: 10, background: 'rgba(255,59,48,.12)', color: 'var(--c-danger)', borderRadius: 6, padding: '1px 7px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/></svg>
                          派单已过期
                        </span>
                      </div>
                      <div style={{ padding: '8px 12px', background: 'var(--c-surface)', display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        {imgs.map(img => <ImgThumb key={img.id} img={img} border="1.5px solid var(--c-danger)" dim={52} h={92} />)}
                        <div style={{ flex: 1, minWidth: 80, fontSize: 11, color: 'var(--c-text-tertiary)', alignSelf: 'center' }}>
                          号码超出72小时有效期 · {imgs[0]?.batchLabel}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div key={key} style={{ border: '1px solid var(--c-danger)', borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
                      <div style={{ padding: '9px 12px', background: 'rgba(255,59,48,.06)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-danger)', flex: 1 }}>{fmtPhone(phone) || '—'}</span>
                        <span style={{ fontSize: 10, background: 'rgba(255,59,48,.12)', color: 'var(--c-danger)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>号码不在库中</span>
                      </div>
                      <div style={{ padding: '8px 12px', background: 'var(--c-surface)', display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        {imgs.map(img => <ImgThumb key={img.id} img={img} border="1.5px solid var(--c-danger)" dim={52} h={92} />)}
                        <div style={{ flex: 1, minWidth: 80 }}>
                          <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginBottom: 6 }}>OCR识别到号码但不在派单库 · {imgs[0]?.batchLabel}</div>
                          <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }}
                            onClick={() => { setManualTarget({ imageId: imgs[0]?.id, groupLabel: imgs[0]?.batchLabel }); setManualPhone(''); setManualMsg(null); }}>
                            手动填写
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <Pagination total={totalInvalid} current={page} onChange={setPage} />
              </>
            )}
          </div>
        );
      })()}

      {/* Tab 内容：失败（识别不出号码 + OCR技术出错）*/}
      {tab === 'failed' && (() => {
        // 合并三类，按原来顺序：singleImgs → notextImgs → faileds
        const allFailed = [...singleImgs, ...notextImgs, ...faileds];
        const totalFailed = allFailed.length;
        const pageFailed = allFailed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
        const singleSet = new Set(singleImgs.map(i => i.id));
        const notextSet = new Set(notextImgs.map(i => i.id));
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {totalFailed === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--c-text-tertiary)', fontSize: 13, padding: 20 }}>无失败图片</p>
            ) : (
              <>
                {totalFailed > PAGE_SIZE && (
                  <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginBottom: 4, textAlign: 'right' }}>
                    共 {totalFailed} 张，显示第 {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalFailed)} 张
                  </div>
                )}
                {pageFailed.map(img => {
                  if (singleSet.has(img.id)) return (
                    <div key={img.id} style={{ border: '1px solid var(--c-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--c-surface)', padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'center' }}>
                      <ImgThumb img={img} dim={40} h={70} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-accent)' }}>
                          {img.ocrPhone || '—'}
                          <span style={{ marginLeft: 8, fontSize: 10, background: 'rgba(255,149,0,0.12)', color: 'var(--c-accent)', borderRadius: 6, padding: '1px 6px', fontWeight: 600 }}>缺少配对图</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', margin: '2px 0 6px' }}>识别到号码，但批次内仅1张 · {img.batchLabel}</div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }}
                            onClick={() => { setManualTarget({ imageId: img.id, groupLabel: img.batchLabel }); setManualPhone(img.ocrPhone || ''); setManualMsg(null); }}>手动填写</button>
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--c-text-secondary)' }}
                            onClick={() => handleReocr(img.id)}>重新识别</button>
                          {remindedIds.has(img.id) ? (
                            <span style={{ fontSize: 11, color: 'var(--c-success)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <Check size={11} strokeWidth={2.5} /> 已提醒
                            </span>
                          ) : (
                            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--c-primary)' }}
                              disabled={remindingId === img.id}
                              onClick={() => handleRemindMissing(img)}>
                              {remindingId === img.id ? '发送中…' : '提醒补图'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                  if (notextSet.has(img.id)) return (
                    <div key={img.id} style={{ border: '1px solid var(--c-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--c-surface)', padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'center' }}>
                      <ImgThumb img={img} dim={40} h={70} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text)' }}>识别不出号码</div>
                        <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', margin: '2px 0 6px' }}>
                          {failText(img, '图片内容未检测到手机号')} · {img.batchLabel}
                          {img.ocrFail?.detail && <div style={{ marginTop: 2, opacity: .8 }}>{img.ocrFail.detail}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }}
                            onClick={() => { setManualTarget({ imageId: img.id, groupLabel: img.batchLabel }); setManualPhone(''); setManualMsg(null); }}>手动填写</button>
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--c-text-secondary)' }}
                            onClick={() => handleReocr(img.id)}>重新识别</button>
                        </div>
                      </div>
                    </div>
                  );
                  // faileds
                  return (
                    <div key={img.id} style={{ border: '1px solid var(--c-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--c-surface)', padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'center' }}>
                      <ImgThumb img={img} dim={40} h={70} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)' }}>识别失败</div>
                        <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', margin: '2px 0 6px' }}>
                          {failText(img, '图片处理出错')} · {img.batchLabel}
                          {img.ocrFail?.detail && <div style={{ marginTop: 2, opacity: .8 }}>{img.ocrFail.detail}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }}
                            onClick={() => { setManualTarget({ imageId: img.id, groupLabel: img.batchLabel }); setManualPhone(''); setManualMsg(null); }}>手动填写</button>
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--c-text-secondary)' }}
                            onClick={() => handleReocr(img.id)}>重新识别</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <Pagination total={totalFailed} current={page} onChange={setPage} />
              </>
            )}
          </div>
        );
      })()}

      {/* 手动填写弹窗（管理员版）— 使用统一 Modal 组件 */}
      {manualTarget && (
        <Modal show={true} onClose={() => { setManualTarget(null); setManualMsg(null); }} title="手动填写手机号">
          <div style={{ padding: '4px 0 8px' }}>
            <p style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 10 }}>
              {manualTarget.groupLabel}<br />图片未能识别到号码，请手动输入：
            </p>
            <div style={{ position: 'relative' }}>
              <input className="input" type="tel" placeholder="输入11位手机号" maxLength={11}
                value={manualPhone}
                onChange={e => { setManualPhone(e.target.value.replace(/\D/g,'').slice(0,11)); setManualMsg(null); }}
                style={{ letterSpacing: '.06em', paddingRight: 44 }}
                autoFocus
              />
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 600,
                color: manualPhone.length === 11 ? 'var(--c-success)' : 'var(--c-text-tertiary)', pointerEvents: 'none' }}>
                {manualPhone.length}/11
              </span>
            </div>
            {manualPhone.length > 0 && manualPhone.length < 11 && (
              <p style={{ fontSize: 11.5, color: 'var(--c-danger)', marginTop: 5 }}>还需再输入 {11 - manualPhone.length} 位</p>
            )}
            {manualMsg && (
              <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, fontSize: 12,
                background: manualMsg.type === 'ok' ? 'rgba(52,199,89,.1)' : manualMsg.type === 'warn' ? 'rgba(255,149,0,.1)' : 'rgba(255,59,48,.1)',
                color: manualMsg.type === 'ok' ? 'var(--c-success)' : manualMsg.type === 'warn' ? 'var(--c-accent)' : 'var(--c-danger)' }}>
                {manualMsg.text}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-outline" style={{ flex: 1, height: 44 }}
                onClick={() => { setManualTarget(null); setManualMsg(null); }}>取消</button>
              <button className="btn btn-primary" style={{ flex: 1, height: 44 }}
                disabled={!/^1[3-9]\d{9}$/.test(manualPhone) || manualLoading}
                onClick={handleManualSubmit}>
                {manualLoading ? '保存中…' : '确认填写'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── 图片放大 Lightbox ──────────────────────────────────────── */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 999,
            background: 'rgba(0,0,0,0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, cursor: 'zoom-out',
          }}
        >
          <img
            src={lightboxUrl} alt=""
            style={{
              maxWidth: '100%', maxHeight: '100%',
              borderRadius: 10, objectFit: 'contain',
              boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
              pointerEvents: 'none',
            }}
          />
          <div style={{
            position: 'absolute', top: 'calc(16px + env(safe-area-inset-top, 0px))', right: 'calc(16px + env(safe-area-inset-right, 0px))',
            width: 36, height: 36, borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: 20, color: '#fff', lineHeight: 1,
          }}>✕</div>
        </div>
      )}
    </div>
  );
}
